import React, { useState } from 'react';
import { useColorTheme } from '../../context/ColorThemeContext';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Download, FileText, ArrowRight, Loader2 } from 'lucide-react';

export default function ExportControls({ canvasRef, formData, format, freeFormElements }) {
  const { colorTheme } = useColorTheme();
  const [exporting, setExporting] = useState(null);

  const gradient = colorTheme === 'purple' ? 'from-purple-600 to-pink-600'
    : colorTheme === 'blue' ? 'from-blue-600 to-sky-500'
    : 'from-emerald-600 to-teal-500';

  const captureCanvas = async () => {
    if (!canvasRef?.current) return null;
    const canvas = await html2canvas(canvasRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      width: format.w,
      height: format.h,
      logging: false,
    });
    return canvas;
  };

  const handleDownloadPNG = async () => {
    setExporting('png');
    try {
      const canvas = await captureCanvas();
      if (!canvas) return;
      const link = document.createElement('a');
      link.download = `${formData.event_title || 'poster'}_${format.id}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('PNG export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  const handleDownloadPDF = async () => {
    setExporting('pdf');
    try {
      const canvas = await captureCanvas();
      if (!canvas) return;
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: format.h > format.w ? 'portrait' : 'landscape',
        unit: 'px',
        format: [format.w, format.h],
      });
      pdf.addImage(imgData, 'PNG', 0, 0, format.w, format.h);
      pdf.save(`${formData.event_title || 'poster'}_${format.id}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="flex-shrink-0 border-t border-slate-200/50 dark:border-slate-800/50 bg-white dark:bg-slate-900 px-6 py-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Output: {format.w} x {format.h}px &middot; {format.label}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadPNG}
            disabled={!!exporting}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            data-testid="download-png-btn"
          >
            {exporting === 'png' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download PNG
          </button>
          <button
            onClick={handleDownloadPDF}
            disabled={!!exporting}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            data-testid="download-pdf-btn"
          >
            {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Download PDF
          </button>
          <button
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r ${gradient} text-white text-sm font-medium hover:opacity-90 transition-opacity`}
            data-testid="create-campaign-btn"
          >
            <ArrowRight className="w-4 h-4" />
            Create Ad Campaign
          </button>
        </div>
      </div>
    </div>
  );
}
